import { useEffect, useRef, useState } from "react";
import {
  Loader2, Upload, Trash2, ArrowUp, ArrowDown, GripVertical,
  LogOut, ExternalLink, Play, ImageIcon, Film,
} from "lucide-react";
import {
  getPortfolio, createPortfolioItem, deletePortfolioItem,
  reorderPortfolio, uploadFile, logout,
} from "./adminApi";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${BACKEND}${u}`);
const TAGS = [
  "Video Reel", "Sale Campaign", "Event Flyer", "Infographic",
  "Insurance Campaign", "Brand Flyer", "Product Launch", "Event Promo", "Service Promo",
];
const VIDEO_EXTS = [".mp4", ".webm"];
const isVideoFile = (f) => f && VIDEO_EXTS.some((e) => f.name.toLowerCase().endsWith(e));

export default function AdminDashboard({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const dragIndex = useRef(null);

  const load = () => {
    setLoading(true);
    getPortfolio()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const persistOrder = (next) => {
    setItems(next);
    reorderPortfolio(next.map((i) => i.id)).catch(() => load());
  };

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    persistOrder(next);
  };

  const onDrop = (to) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === to) return;
    move(from, to);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item from the Work section?")) return;
    setItems((prev) => prev.filter((i) => i.id !== id));
    try {
      await deletePortfolioItem(id);
    } catch {
      load();
    }
  };

  return (
    <div className="min-h-screen bg-[#050B26] text-white antialiased">
      <header className="border-b border-white/10 sticky top-0 z-10 bg-[#050B26]/95 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#5b80ff] font-semibold">
              Knight Media Group
            </p>
            <h1 className="font-bold text-xl">Portfolio Admin</h1>
          </div>
          <div className="flex items-center gap-4">
            <a href="/#work" target="_blank" rel="noreferrer"
              className="text-white/60 hover:text-white text-sm inline-flex items-center gap-1.5"
              data-testid="view-site-link">
              View site <ExternalLink size={14} />
            </a>
            <span className="text-white/40 text-sm hidden sm:inline">{user?.email}</span>
            <button
              onClick={() => logout().then(() => window.location.reload())}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm"
              data-testid="admin-logout-btn">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10 grid lg:grid-cols-[1fr_1.3fr] gap-10">
        <UploadForm onCreated={load} />

        <section data-testid="admin-items">
          <h2 className="font-bold text-lg mb-1">Current items</h2>
          <p className="text-white/50 text-sm mb-5">
            Drag the handle or use the arrows to reorder. Top items appear first on the site.
          </p>
          {loading ? (
            <div className="flex items-center gap-2 text-white/60">
              <Loader2 className="animate-spin" size={18} /> Loading…
            </div>
          ) : items.length === 0 ? (
            <p className="text-white/50 text-sm">No items yet — add your first one.</p>
          ) : (
            <ul className="space-y-3">
              {items.map((it, i) => (
                <ItemRow
                  key={it.id}
                  item={it}
                  index={i}
                  total={items.length}
                  onDragStart={() => (dragIndex.current = i)}
                  onDrop={() => onDrop(i)}
                  onUp={() => move(i, i - 1)}
                  onDown={() => move(i, i + 1)}
                  onDelete={() => remove(it.id)}
                />
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

function ItemRow({ item, index, total, onDragStart, onDrop, onUp, onDown, onDelete }) {
  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className="flex items-center gap-3 bg-white/[0.05] border border-white/10 rounded-xl p-3"
      data-testid="admin-item-row"
    >
      <GripVertical className="text-white/30 cursor-grab shrink-0" size={18} />
      <div className="size-14 rounded-lg overflow-hidden bg-black shrink-0 relative">
        {item.image ? (
          <img src={resolveUrl(item.image)} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30">
            <Film size={18} />
          </div>
        )}
        {item.type === "video" && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Play size={14} className="text-white" fill="currentColor" />
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm truncate" data-testid="admin-item-title">{item.title}</p>
        <p className="text-white/50 text-xs flex items-center gap-1.5 mt-0.5">
          {item.type === "video" ? <Film size={12} /> : <ImageIcon size={12} />}
          {item.tag}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={onUp} disabled={index === 0}
          className="size-8 rounded-lg bg-white/5 hover:bg-white/15 disabled:opacity-30 flex items-center justify-center"
          data-testid="admin-move-up" aria-label="Move up">
          <ArrowUp size={15} />
        </button>
        <button onClick={onDown} disabled={index === total - 1}
          className="size-8 rounded-lg bg-white/5 hover:bg-white/15 disabled:opacity-30 flex items-center justify-center"
          data-testid="admin-move-down" aria-label="Move down">
          <ArrowDown size={15} />
        </button>
        <button onClick={onDelete}
          className="size-8 rounded-lg bg-white/5 hover:bg-red-500/80 flex items-center justify-center"
          data-testid="admin-delete-btn" aria-label="Delete">
          <Trash2 size={15} />
        </button>
      </div>
    </li>
  );
}

function UploadForm({ onCreated }) {
  const [title, setTitle] = useState("");
  const [tag, setTag] = useState("");
  const [file, setFile] = useState(null);
  const [poster, setPoster] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const fileInput = useRef(null);
  const posterInput = useRef(null);
  const video = isVideoFile(file);

  const reset = () => {
    setTitle(""); setTag(""); setFile(null); setPoster(null); setProgress(0);
    if (fileInput.current) fileInput.current.value = "";
    if (posterInput.current) posterInput.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim() || !tag.trim() || !file) {
      setError("Title, tag and a file are required.");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const main = await uploadFile(file, setProgress);
      let posterUrl = "";
      if (video && poster) {
        const p = await uploadFile(poster);
        posterUrl = p.url;
      }
      await createPortfolioItem({
        title: title.trim(),
        tag: tag.trim(),
        type: main.type,
        image: main.type === "image" ? main.url : posterUrl,
        video: main.type === "video" ? main.url : "",
      });
      reset();
      onCreated();
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h2 className="font-bold text-lg mb-1">Add to Work</h2>
      <p className="text-white/50 text-sm mb-5">Upload a flyer or a video reel. New items show first.</p>
      <form onSubmit={submit} className="space-y-4" data-testid="admin-upload-form">
        <Labeled label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Nikki's Next Level — Summer Sale"
            className={inputCls} data-testid="upload-title-input" />
        </Labeled>
        <Labeled label="Tag">
          <input value={tag} onChange={(e) => setTag(e.target.value)} list="tag-options"
            placeholder="e.g. Video Reel" className={inputCls} data-testid="upload-tag-input" />
          <datalist id="tag-options">
            {TAGS.map((t) => <option key={t} value={t} />)}
          </datalist>
        </Labeled>
        <Labeled label="File (image or MP4/WebM video)">
          <input ref={fileInput} type="file" accept="image/*,video/mp4,video/webm"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-white/70 file:mr-3 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:bg-[#1440E1] file:text-white file:font-semibold hover:file:bg-[#1033B3] file:cursor-pointer"
            data-testid="upload-file-input" />
          <p className="text-white/35 text-xs mt-1.5">
            Tip: iPhone .mov / HEVC isn't web-playable — export as "Most Compatible" (MP4).
          </p>
        </Labeled>
        {video && (
          <Labeled label="Poster image (optional)">
            <input ref={posterInput} type="file" accept="image/*"
              onChange={(e) => setPoster(e.target.files?.[0] || null)}
              className="block w-full text-sm text-white/70 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-white/10 file:text-white file:font-semibold hover:file:bg-white/20 file:cursor-pointer"
              data-testid="upload-poster-input" />
          </Labeled>
        )}

        {error && <p className="text-[#ff9b9b] text-sm" data-testid="upload-error">{error}</p>}

        {busy && (
          <div className="h-2 rounded-full bg-white/10 overflow-hidden" data-testid="upload-progress">
            <div className="h-full bg-[#1440E1] transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}

        <button type="submit" disabled={busy}
          className="btn-kmg w-full inline-flex items-center justify-center gap-2 bg-[#1440E1] hover:bg-[#1033B3] disabled:opacity-60 text-white px-6 py-3.5 rounded-full font-bold tracking-wide"
          data-testid="upload-submit-btn">
          {busy ? <><Loader2 size={18} className="animate-spin" /> Uploading… {progress}%</>
                : <><Upload size={18} /> Add to portfolio</>}
        </button>
      </form>
    </section>
  );
}

const inputCls =
  "w-full bg-white/[0.06] border border-white/15 rounded-[10px] px-4 py-3 text-[15px] text-white placeholder:text-white/35 outline-none focus:border-[#1440e1] focus:shadow-[0_0_0_3px_rgba(20,64,225,0.25)] transition";

function Labeled({ label, children }) {
  return (
    <div>
      <label className="block text-white/80 text-sm font-medium mb-2">{label}</label>
      {children}
    </div>
  );
}
